import { useForm } from "react-hook-form";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface TireFormSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (data: any) => void;
  tire?: any;
  companyId: string;
}

export function TireFormSheet({ open, onOpenChange, onSave, tire, companyId }: TireFormSheetProps) {
  const { register, handleSubmit, setValue, watch } = useForm({
    defaultValues: {
      tire_type: 'new',
      tire_construction: 'radial',
      ...tire
    }
  });

  const onSubmit = (data: any) => {
    // Mapear nomes de campos do form para o banco de dados
    const mappedData = {
      ...data,
      speed_rating: data.speed_index,
      purchase_price: data.price_brl ? parseFloat(data.price_brl) : undefined,
      initial_tread_depth: data.tread_depth_mm ? parseFloat(data.tread_depth_mm) : undefined,
    };
    
    // Remover campos do form que não existem no DB
    delete mappedData.speed_index;
    delete mappedData.price_brl;
    delete mappedData.tread_depth_mm;
    
    onSave(mappedData);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:w-[550px] p-0 overflow-hidden flex flex-col">
        <SheetHeader>
          <SheetTitle>{tire ? 'Editar Pneu' : 'Novo Pneu'}</SheetTitle>
        </SheetHeader>

        <ScrollArea className="flex-1 -mx-6 px-6">
          <form id="tire-form" onSubmit={handleSubmit(onSubmit)} className="space-y-6 py-4">
            <div className="space-y-4">
              <h4 className="text-sm font-medium border-b pb-2">Identificação</h4>
              
              <div className="space-y-2">
                <Label>Número de Série *</Label>
                <Input {...register('serial_number', { required: true })} />
              </div>

              <div className="grid gap-4 grid-cols-2">
                <div className="space-y-2">
                  <Label>Marca *</Label>
                  <Input {...register('brand', { required: true })} />
                </div>

                <div className="space-y-2">
                  <Label>Modelo *</Label>
                  <Input {...register('model', { required: true })} />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Medida *</Label>
                <Input {...register('size', { required: true })} placeholder="Ex: 295/80R22.5" />
              </div>
            </div>

            <div className="space-y-4">
              <h4 className="text-sm font-medium border-b pb-2">Especificações</h4>
              
              <div className="grid gap-4 grid-cols-2">
                <div className="space-y-2">
                  <Label>Tipo de Pneu</Label>
                  <Select onValueChange={(v) => setValue('tire_type', v)} defaultValue={tire?.tire_type || 'new'}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">Novo</SelectItem>
                      <SelectItem value="retreaded">Recapado</SelectItem>
                      <SelectItem value="used">Usado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Construção</Label>
                  <Select onValueChange={(v) => setValue('tire_construction', v)} defaultValue={tire?.tire_construction || 'radial'}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="radial">Radial</SelectItem>
                      <SelectItem value="diagonal">Diagonal</SelectItem>
                      <SelectItem value="runflat">Run-flat</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid gap-4 grid-cols-2">
                <div className="space-y-2">
                  <Label>Índice de Carga</Label>
                  <Input {...register('load_index')} placeholder="Ex: 150" />
                </div>

                <div className="space-y-2">
                  <Label>Índice de Velocidade</Label>
                  <Input {...register('speed_index')} placeholder="Ex: L" />
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h4 className="text-sm font-medium border-b pb-2">Compra</h4>
              
              <div className="space-y-2">
                <Label>DOT (Data Fab.)</Label>
                <Input {...register('dot_code')} placeholder="Ex: 2323" />
              </div>

              <div className="grid gap-4 grid-cols-2">
                <div className="space-y-2">
                  <Label>Preço de Compra (R$)</Label>
                  <Input {...register('price_brl')} type="number" step="0.01" placeholder="0.00" />
                </div>

                <div className="space-y-2">
                  <Label>Profundidade Inicial (mm)</Label>
                  <Input {...register('tread_depth_mm')} type="number" step="0.1" placeholder="Ex: 16.0" />
                </div>
              </div>
            </div>
          </form>
        </ScrollArea>

        <SheetFooter className="border-t pt-4 mt-auto">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button type="submit" form="tire-form">
            {tire ? 'Atualizar' : 'Cadastrar'}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
